<template>
    <div>
        <div class="d-flex">
            <ButtonIcon class="mb-2" variant="outline-primary" icon="chevron-left" :to="{ name: 'CampaignsDetailsTabIndex', params: { id: $route.params.id, tab: $route.params.tab } }">
                {{ $t('returnToListView') }}
            </ButtonIcon>
        </div>

        <div v-if="loading" class="d-flex justify-content-center">
            <b-spinner />
        </div>

        <form v-if="!loading && feed" class="mb-3">

            <PageHeader>
                {{ $t('feed') }} {{ feed.name && `"${feed.name}"` }}
                <template #side>
                    <div class="d-flex gap-3">
                        <ButtonIcon
                            v-if="feed.active"
                            class="width-min-100"
                            variant="danger"
                            :loading="requestFeed.getLoading('active-state')"
                            :disabled="busy"
                            hide-label-while-loading
                            @click="patchActiveState(false)"
                        >
                            {{ $t('deactivate') }}
                        </ButtonIcon>
                        <ButtonIcon
                            v-else
                            class="width-min-100"
                            variant="success"
                            :loading="requestFeed.getLoading('active-state')"
                            :disabled="busy"
                            hide-label-while-loading
                            @click="patchActiveState(true)"
                        >
                            {{ $t('activate') }}
                        </ButtonIcon>
                        <ButtonIcon
                            class="min-w-300"
                            variant="primary"
                            hide-label-while-loading
                            :loading="requestFeedProcess.getLoading()"
                            :disabled="busy"
                            @click="reprocess()"
                        >
                            {{ $t('processAgain') }}
                        </ButtonIcon>
                    </div>
                </template>
            </PageHeader>

            <div class="grid-template">

                <div class="border"></div>

                <template v-if="feed.createdAt">
                    <div class="label">{{ $t('created2') }}</div>
                    <div class="value add-after">{{ $format.dateAndHour(feed.createdAt, 'dd-MM-yyyy', 'HH:mm:ss') }}</div>
                    <div></div>
                </template>

                <template v-if="feed.updatedAt">
                    <div class="label">{{ $t('updated2') }}</div>
                    <div class="value add-after">{{ $format.dateAndHour(feed.updatedAt, 'dd-MM-yyyy', 'HH:mm:ss') }}</div>
                    <div></div>
                </template>

                <template v-if="feed.offerFeed && feed.offerFeed.processStartedAt">
                    <div class="label">{{ $t('processStartedAt') }}</div>
                    <div class="value add-after">{{ $format.dateAndHour(feed.offerFeed.processStartedAt, 'dd-MM-yyyy', 'HH:mm:ss') }}</div>
                    <div></div>
                </template>

                <template v-if="feed.offerFeed && feed.offerFeed.processEndedAt">
                    <div class="label">{{ $t('processEndedAt') }}</div>
                    <div class="value add-after">{{ $format.dateAndHour(feed.offerFeed.processEndedAt, 'dd-MM-yyyy', 'HH:mm:ss') }}</div>
                    <div></div>
                </template>

                <template v-if="feed.offerFeed">
                    <div class="label">{{ $t('report') }}</div>
                    <div class="value add-after">{{ feed.offerFeed.report }}</div>
                    <div></div>
                </template>

                <template v-if="feed.offerFeed">
                    <div class="label">{{ $t('processingStatus') }}</div>
                    <div class="value add-after">{{ feedStatuses[feed.offerFeed.status] }}</div>
                    <div></div>
                </template>

                <div class="label">{{ $t('feedStatus') }}</div>
                <div class="value add-after">
                    <template v-if="feed.active">{{ $t('active') }}</template>
                    <template v-else>{{ $t('inactive') }}</template>
                </div>
                <div></div>

            </div>

        </form>

        <form v-if="!loading" class="d-flex flex-column gap-4">

            <div class="grid-template">

                <div class="border"></div>

                <div class="label">{{ $t('name') }}</div>
                <div class="value add-after">
                    <FormValidator :validator="$v.form.name" form-group v-slot="slotProps" :attribute="$t('name')">
                        <b-form-input v-model="form.name" :disabled="busy" :state="slotProps.state" />
                    </FormValidator>
                </div>
                <div></div>

                <div class="label">{{ $t('xmlFile') }}</div>
                <div class="value">
                    <FormValidator :validator="$v.form.xmlUrl" form-group v-slot="slotProps" :attribute="$t('xmlFile')">
                        <b-form-input v-model="form.xmlUrl" :disabled="busy" :state="slotProps.state" />
                    </FormValidator>
                </div>
                <div>
                    <ButtonIcon
                        icon="external-link"
                        variant="link"
                        size="sm"
                        flip-icon
                        target="_blank"
                        :href="form.xmlUrl"
                    >
                        {{ $t('open') }}
                    </ButtonIcon>
                </div>

                <div class="label">{{ $t('authorization') }} ({{ $t('optional') }})</div>
                <div class="value">
                    <b-form-input v-model="form.httpAuthUsername" placeholder="użytkownik" :disabled="busy" />
                    <b-form-input v-model="form.httpAuthPassword" placeholder="hasło" :disabled="busy" />
                </div>
                <div></div>

                <div class="label">{{ $t('format') }}</div>
                <div class="value add-after">
                    <FormValidator :validator="$v.form.categoryItems" form-group v-slot="slotProps" :attribute="$t('format')">
                        <b-form-select
                            v-model="form.categoryItems"
                            :options="categoryItems"
                            add-null-option
                            :disabled="busy"
                            text-field="categoryName"
                            value-field="id"
                            :state="slotProps.state"
                        />
                    </FormValidator>
                </div>
                <div></div>

                <div class="label">{{ $t('parser') }}</div>
                <div class="value add-after">
                    <FormValidator :validator="$v.form.parser" form-group v-slot="slotProps" :attribute="$t('parser')">
                        <b-form-select
                            v-model="form.parser"
                            :options="feedParsers"
                            add-null-option
                            :disabled="busy"
                            text-field="label"
                            value-field="value"
                            :state="slotProps.state"
                        />
                    </FormValidator>
                </div>
                <div></div>

                <div class="label">{{ $t('redirectUrl') }} ({{ $t('publishers') }})</div>
                <div class="value add-after"><b-form-input v-model="form.extensionName" :disabled="busy" /></div>
                <div></div>

                <div class="label">{{ $t('redirectUrl') }} ({{ $t('producers') }})</div>
                <div class="value add-after"><b-form-input v-model="form.producerExtensionName" :disabled="busy" /></div>
                <div></div>

                <div class="label">{{ $t('parameterName') }}</div>
                <div class="value add-after"><b-form-input v-model="form.extensionParam" :disabled="busy" /></div>
                <div></div>

                <div class="label">{{ $t('ruleFeedPrice') }}</div>
                <div class="value add-after">
                    <b-form-checkbox
                        v-model="form.excludeFeedPriceRule"
                        switch
                        size="lg"
                        :value="false"
                        :unchecked-value="true"
                        :disabled="busy"
                    />
                </div>
                <div></div>

                <div class="label">{{ $t('offerLifetime') }}</div>
                <div class="value add-after"><b-form-input v-model.number="form.offerLifetime" :disabled="busy" type="number" /></div>
                <div>{{ $t('inHours') }}</div>

            </div>

            <div class="d-flex gap-4">

                <div class="flex-grow-1 flex-shrink-0 w-50">
                    <div class="label">{{ $t('processingOptions') }}</div>
                    <JSONeditor v-model="form.processorOptions" :plus="false" height="400px" :disabled="busy" />
                </div>

                <div class="flex-grow-1 flex-shrink-0 w-50">
                    <div class="label">{{ $t('queryOptions') }}</div>
                    <JSONeditor v-model="form.requestOptions" :plus="false" height="400px" :disabled="busy" />
                </div>

            </div>

            <ButtonIcon
                class="mr-auto"
                variant="primary"
                :disabled="busy"
                :loading="requestFeed.getLoading('update')"
                hide-label-while-loading
                @click="save()"
            >
                {{ $t('saveChanges') }}
            </ButtonIcon>

        </form>

    </div>
</template>



<script>
    import { required } from 'vuelidate/lib/validators';
    import JSONeditor from 'v-jsoneditor';
    import FormValidator from '@/components/common/FormValidator.vue';
    import { deepMergeWithFilter } from '@/utils/util';

    export default {
        name: 'AdminCampaignsFeedEdit',
        components: {
            JSONeditor,
            FormValidator,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                campaignId: Number.parseInt(this.$route.params.id),
                id: Number.parseInt(this.$route.params.itemId),
                form: {
                    offerLifetime: null,
                    name: '',
                    categoryItems: null,
                    parser: null,
                    extensionName: '',
                    producerExtensionName: '',
                    extensionParam: '',
                    excludeFeedPriceRule: true,
                    xmlUrl: '',
                    httpAuthUsername: '',
                    httpAuthPassword: '',
                    processorOptions: {},
                    requestOptions: {},
                },
                feed: null,
                requestCategoryItems: this.$req('productFeeds/CategoryItems'),
                requestFeedParsers: this.$req('productFeeds/FeedParsers'),
                requestFeedStatuses: this.$req('offers/FeedStatuses'),
                requestFeed: this.$req('productFeeds/Feed'),
                requestFeedProcess: this.$req('productFeeds/FeedProcess'),
            };
        },
        validations() {
            return {
                form: {
                    name: { required },
                    categoryItems: { required },
                    parser: { required },
                    xmlUrl: { required },
                },
            };
        },
        computed: {
            parentRoute() {
                return this.$route.matched.slice(-2)[0];
            },
            categoryItems() {
                return this.requestCategoryItems.getData() || [];
            },
            feedParsers() {
                return this.requestFeedParsers.getData() || [];
            },
            feedStatuses() {
                return (this.requestFeedStatuses.getData() || []).reduce((acc, cur) => ({ ...acc, [cur.value]: cur.label }), {});
            },
            error() {
                return this.requestFeed.getError();
            },
            loading() {
                return this.requestFeed.getLoading();
            },
            busy() {
                return this.requestFeed.getLoading() || this.requestFeed.getLoading('patch') || this.requestFeed.getLoading('post') || this.requestFeed.getLoading('active-state') || this.requestFeedProcess.getLoading();
            },
        },
        methods: {
            fetchCategoryItems() {
                this.requestCategoryItems.get().catch(() => {});
            },
            fetchFeedParsers() {
                this.requestFeedParsers.get().catch(() => {});
            },
            fetchFeedStatuses() {
                this.requestFeedStatuses.get().catch(() => {});
            },
            fetchFeed() {
                this.requestFeed
                    .get({ urlParams: { id: this.id } })
                    .then(feed => {
                        this.setFeed(feed);
                        this.initFormValues();
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            patchActiveState(state) {
                this.requestFeed
                    .id('active-state')
                    .patch({ urlParams: { id: this.id }, requestParams: { active: state } })
                    .then(this.setFeed)
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            reprocess() {
                this.requestFeedProcess
                    .put({ urlParams: { id: this.id } })
                    .then(this.setFeed)
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            save() {
                this.$v.form.$touch();
                if (this.$v.form.$invalid) return;
                const urlParams = this.id ? { id: this.id } : { campaignId: this.campaignId };
                const apiParams = {
                    urlParams,
                    requestParams: this.form,
                };
                const type = this.feed ? 'patch' : 'post';
                this.requestFeed
                    .id(type)
                    [type](apiParams)
                    .then(feed => {
                        this.$toastr.success(this.$t('dataSavedSuccessfully'));
                        this.setFeed(feed);
                        this.initFormValues();
                        if (type === 'post') {
                            this.id = feed.id;
                            this.$router.replace({ name: 'CampaignsDetailsTabEdit', params: { id: this.campaignId, tab: this.$route.params.tab, itemId: feed.id } }).then(this.parentComponent.fetchData);
                        }
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            initFormValues() {
                this.form = deepMergeWithFilter(this.form, this.feed);
                this.form.categoryItems = this.feed.categoryItem.id;
            },
            setFeed(feed) {
                this.feed = feed;
            },
        },
        created() {
            this.fetchCategoryItems();
            this.fetchFeedParsers();
            this.fetchFeedStatuses();
            if (this.id) this.fetchFeed();
        },
    };
</script>



<style lang="scss" scoped>
    form {
        .label {
            font-weight: bold;
        }
    }

    .grid-template {
        display: grid;
        grid-template-columns: minmax(25%, max-content) auto min-content;
        gap: 1rem;

        > div {
            display: flex;
            gap: 1rem;
            align-items: center;

            fieldset {
                width: 100%;
                margin: 0;
            }
        }

        .border {
            grid-column: 1/-1;
        }
    }
</style>
